import React from "react";
import { ToasterToast } from "@/common/types/types";
import { actionTypes, TOAST_LIMIT } from "@/common/config/constants";

// --- ID generator ---
let count = 0;
const genId = () => {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

// --- Types ---
type ActionType = typeof actionTypes;

type Action =
  | { type: ActionType["ADD_TOAST"]; toast: ToasterToast }
  | { type: ActionType["UPDATE_TOAST"]; toast: Partial<ToasterToast> & { id: string } }
  | { type: ActionType["DISMISS_TOAST"]; toastId?: string }
  | { type: ActionType["REMOVE_TOAST"]; toastId?: string };

interface State {
  toasts: ToasterToast[];
}

// --- Global state and listeners ---
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
let memoryState: State = { toasts: [] };
const listeners: Array<(state: State) => void> = [];

// --- Reducer ---
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case actionTypes.ADD_TOAST: {
      const newToasts = [...state.toasts, action.toast].slice(-TOAST_LIMIT);
      return { ...state, toasts: newToasts };
    }

    case actionTypes.UPDATE_TOAST: {
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };
    }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action;

      const updatedToasts = state.toasts.map((t) =>
        toastId ? (t.id === toastId ? { ...t, open: false } : t) : { ...t, open: false }
      );

      if (toastId) queueRemove(toastId);
      else updatedToasts.forEach((t) => queueRemove(t.id));

      return { ...state, toasts: updatedToasts };
    }

    case actionTypes.REMOVE_TOAST: {
      if (action.toastId) {
        const timeout = toastTimeouts.get(action.toastId);
        if (timeout) clearTimeout(timeout);
        toastTimeouts.delete(action.toastId);
        return {
          ...state,
          toasts: state.toasts.filter((t) => t.id !== action.toastId),
        };
      }

      // Remove all
      toastTimeouts.forEach(clearTimeout);
      toastTimeouts.clear();
      return { ...state, toasts: [] };
    }

    default:
      return state;
  }
};

// --- Dispatch function ---
const dispatch = (action: Action) => {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

// --- Queue toast for removal ---
const queueRemove = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: actionTypes.REMOVE_TOAST, toastId });
  }, 5000);

  toastTimeouts.set(toastId, timeout);
}

// --- Toast API ---
type ToastOptions = Omit<ToasterToast, "id">;

const toast = (props: ToastOptions) => {
  const id = genId();

  const dismiss = () => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });
  const update = (updateProps: Partial<ToasterToast>) =>
    dispatch({ type: actionTypes.UPDATE_TOAST, toast: { ...updateProps, id } });

  // Add toast
  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss();
      },
    },
  });

  // Auto-dismiss
  queueRemove(id);

  return { id, dismiss, update };
}

// --- React hook ---
const useToast = () => {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  };
}

export { useToast, toast };

